/**
 * ARCHITECTURE: Customer Portal v2 Dashboard (Task 47 Phase 4)
 *
 * Single dashboard for both customers and partners. Two data sources
 * branch on auth mode (Q3 in the migration map: same component, partner
 * is a less-privileged role):
 *
 *   - Customer (Bearer token, useAuth().organizations) → already loaded
 *     post-login, just normalise into the card shape.
 *   - Partner (cookie auth + `partner_authenticated` localStorage flag)
 *     → fetch via partnerApiService.getOrganisations() (OQ1: existing
 *     endpoint, no new server work).
 *
 * Layout: <Header /> top, compact 2-column org-card grid, click → opens
 * <OrgDetailPanel /> as a slide-over (Q6) driven by `?org=<id>` URL
 * search param (Q2 — implement deep link now). User menu opens
 * <UserMenuDrawer /> which carries theme + sign-out, replacing the
 * dedicated /settings page (Q5).
 *
 * Org Detail data (subscription / usage / invoices) is fetched lazily
 * on panel open using the existing apiService methods. Each open is a
 * fresh fetch — simpler than a cache, and the surface is small.
 *
 * Partner-only "Create organisation" button surfaces a modal that
 * re-uses partnerApiService.createOrganisation, replacing the inline
 * create form on the deleted PartnerDashboard.
 */
import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  apiService,
  partnerApiService,
  type PartnerOrganisation,
} from '../utils/api';
import type { Organization } from '../types';
import Header from '../components/Header';
import UserMenuDrawer from '../components/UserMenuDrawer';
import OrgDetailPanel, {
  type OrgDetailData,
} from '../components/OrgDetailPanel';
import { EmptyState, LoadingSkeleton, ErrorState } from '@repro/ui';

type Role = 'customer' | 'partner';

interface OrgCard {
  id: string;
  name: string;
  email: string;
  planLabel: string;
  statusLabel: string;
  statusVariant: 'success' | 'warning' | 'error' | 'ghost';
}

const STATUS_VARIANTS: Record<string, OrgCard['statusVariant']> = {
  active: 'success',
  pending: 'warning',
  pending_payment: 'warning',
  suspended: 'error',
  cancelled: 'ghost',
};

function statusVariant(status?: string): OrgCard['statusVariant'] {
  if (!status) return 'ghost';
  return STATUS_VARIANTS[status] ?? 'ghost';
}

function customerToCard(org: Organization): OrgCard {
  return {
    id: org.id,
    name: org.name,
    email: org.billing_email,
    planLabel: org.plan_id,
    statusLabel: org.planStatus ?? 'unknown',
    statusVariant: statusVariant(org.planStatus),
  };
}

function partnerToCard(org: PartnerOrganisation): OrgCard {
  return {
    id: org.id,
    name: org.name,
    email: org.billingEmail,
    planLabel: org.planId,
    statusLabel: org.planStatus ?? 'unknown',
    statusVariant: statusVariant(org.planStatus),
  };
}

function formatGbp(amount: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(amount);
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { email: customerEmail, organizations, logout } = useAuth();

  const partnerAuthed =
    typeof window !== 'undefined' &&
    localStorage.getItem('partner_authenticated') === 'true';
  const role: Role = customerEmail ? 'customer' : 'partner';

  const [orgs, setOrgs] = useState<OrgCard[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [orgsError, setOrgsError] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [partnerEmail, setPartnerEmail] = useState<string | null>(null);

  const selectedOrgId = searchParams.get('org');
  const [panelData, setPanelData] = useState<OrgDetailData | undefined>();
  const [panelLoading, setPanelLoading] = useState(false);
  const [panelError, setPanelError] = useState<string | null>(null);

  // -------------------------------------------------------------------------
  // Auth gate — bounce back to /login if neither customer nor partner
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!customerEmail && !partnerAuthed) {
      navigate('/login');
    }
  }, [customerEmail, partnerAuthed, navigate]);

  // -------------------------------------------------------------------------
  // Load org list
  // -------------------------------------------------------------------------

  const loadOrgs = useCallback(async () => {
    setOrgsLoading(true);
    setOrgsError(null);
    try {
      if (role === 'customer') {
        setOrgs(organizations.map(customerToCard));
      } else {
        const [partnerOrgs, profile] = await Promise.all([
          partnerApiService.getOrganisations(),
          partnerApiService.getProfile().catch(() => null),
        ]);
        setOrgs(partnerOrgs.map(partnerToCard));
        if (profile?.success && profile?.data?.email) {
          setPartnerEmail(profile.data.email);
        }
      }
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to load organisations';
      if (msg.includes('401') || msg.includes('403')) {
        localStorage.removeItem('partner_authenticated');
        navigate('/login');
        return;
      }
      setOrgsError(msg);
    } finally {
      setOrgsLoading(false);
    }
  }, [role, organizations, navigate]);

  useEffect(() => {
    loadOrgs();
  }, [loadOrgs]);

  // -------------------------------------------------------------------------
  // Load panel data when ?org= changes
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!selectedOrgId) {
      setPanelData(undefined);
      setPanelError(null);
      return;
    }

    let cancelled = false;
    (async () => {
      setPanelLoading(true);
      setPanelError(null);
      try {
        const [orgData, subscription, invoices] = await Promise.all([
          apiService.getOrganization(selectedOrgId).catch(() => null),
          apiService.getSubscription(selectedOrgId).catch(() => null),
          apiService.getInvoices(selectedOrgId, 5).catch(() => []),
        ]);
        if (cancelled) return;

        const card = orgs.find((o) => o.id === selectedOrgId);

        const usersMax = orgData?.settings?.maxUsers ?? 'unlimited';
        const projectsMax = orgData?.maxProjects ?? 'unlimited';

        const data: OrgDetailData = {
          id: selectedOrgId,
          name: orgData?.name ?? card?.name ?? 'Organisation',
          email: orgData?.billing_email ?? card?.email ?? '',
          subscription: subscription
            ? {
                planName: orgData?.plan_id ?? card?.planLabel ?? '—',
                planPrice: subscription.plan?.amount
                  ? `${formatGbp(subscription.plan.amount / 100)} / ${
                      subscription.plan.interval ?? 'month'
                    }`
                  : 'Free',
                renewsAt: subscription.current_period_end
                  ? new Date(
                      subscription.current_period_end * 1000,
                    ).toLocaleDateString('en-GB')
                  : 'N/A',
                paymentMethod: subscription.cancel_at_period_end
                  ? 'Cancels at period end'
                  : 'Active',
              }
            : undefined,
          usage: {
            users: { current: 0, max: usersMax },
            projects: {
              current: orgData?.currentProjects ?? 0,
              max: projectsMax,
            },
            storageGb: 0,
          },
          invoices: (invoices ?? []).slice(0, 5).map((inv) => ({
            id: inv.id,
            statusLabel: inv.status ?? 'unknown',
            statusVariant:
              inv.status === 'paid'
                ? 'success'
                : inv.status === 'open'
                  ? 'warning'
                  : 'error',
            dateLabel: inv.created
              ? new Date(inv.created * 1000).toLocaleDateString('en-GB')
              : '—',
            amountLabel: formatGbp((inv.amount_due ?? 0) / 100),
            hostedUrl: inv.hosted_invoice_url ?? undefined,
          })),
        };

        setPanelData(data);
      } catch (err) {
        if (cancelled) return;
        setPanelError(
          err instanceof Error ? err.message : 'Failed to load organisation',
        );
      } finally {
        if (!cancelled) setPanelLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedOrgId, orgs]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const openOrg = (id: string) => {
    setSearchParams({ org: id }, { replace: false });
  };

  const closeOrg = () => {
    setSearchParams({}, { replace: false });
  };

  const handleSignOut = async () => {
    if (role === 'customer') {
      logout();
    } else {
      try {
        await partnerApiService.logout();
      } catch {
        // best-effort; carry on with local clear
      }
      localStorage.removeItem('partner_authenticated');
    }
    navigate('/login');
  };

  const handleManageSubscription = async () => {
    if (!selectedOrgId) return;
    try {
      const url = await apiService.createBillingPortalSession(selectedOrgId);
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setPanelError('Failed to open billing portal');
    }
  };

  const handleCancelSubscription = async () => {
    if (!selectedOrgId) return;
    if (
      !confirm('Cancel this subscription at the end of the current period?')
    ) {
      return;
    }
    const ok = await apiService.cancelSubscription(selectedOrgId, true);
    if (ok) {
      // Re-fetch panel data to reflect cancellation state
      setSearchParams({ org: selectedOrgId }, { replace: true });
    } else {
      setPanelError('Failed to cancel subscription');
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const userEmail = customerEmail ?? partnerEmail ?? 'Account';
  const roleLabel =
    role === 'customer' ? 'Customer' : 'Partner — managing organisations';

  return (
    <div className="min-h-screen bg-base-200">
      <Header
        userEmail={userEmail}
        orgCount={orgs.length}
        currentOrgName={orgs.length === 1 ? orgs[0].name : null}
        onUserMenu={() => setDrawerOpen(true)}
      />

      <main className="mx-auto max-w-5xl px-6 py-10">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-base-content">
              Your organisations
            </h1>
            <p className="text-sm text-base-content/60">
              {role === 'partner'
                ? 'Organisations you manage on behalf of customers.'
                : 'Manage subscriptions and billing for the organisations you have access to.'}
            </p>
          </div>
          {role === 'partner' && (
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="btn btn-primary"
            >
              Create organisation
            </button>
          )}
        </div>

        {orgsLoading && <LoadingSkeleton rows={4} className="max-w-md" />}

        {orgsError && !orgsLoading && (
          <ErrorState body={orgsError} onRetry={loadOrgs} />
        )}

        {!orgsLoading && !orgsError && orgs.length === 0 && (
          <EmptyState
            title={
              role === 'partner'
                ? 'No organisations assigned'
                : 'No organisations yet'
            }
            body={
              role === 'partner'
                ? 'Click "Create organisation" to add one.'
                : 'Contact support if you believe this is an error.'
            }
          />
        )}

        {!orgsLoading && !orgsError && orgs.length > 0 && (
          <div
            className="grid gap-4 sm:grid-cols-2"
            role="list"
            aria-label="Your organisations"
          >
            {orgs.map((org) => (
              <button
                key={org.id}
                type="button"
                onClick={() => openOrg(org.id)}
                className="flex flex-col gap-2 rounded-box border border-base-300 bg-base-100 p-5 text-left shadow-sm transition hover:border-primary/50 hover:shadow-md"
                role="listitem"
                aria-label={`View ${org.name}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-base font-semibold text-base-content">
                      {org.name}
                    </p>
                    <p className="text-sm text-base-content/60">{org.email}</p>
                  </div>
                  <span className={`badge badge-${org.statusVariant}`}>
                    {org.statusLabel}
                  </span>
                </div>
                <p className="text-sm text-base-content/70">
                  Plan: <span className="font-medium">{org.planLabel}</span>
                </p>
              </button>
            ))}
          </div>
        )}
      </main>

      <UserMenuDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        userEmail={userEmail}
        roleLabel={roleLabel}
        onSignOut={handleSignOut}
      />

      <OrgDetailPanel
        open={!!selectedOrgId}
        onClose={closeOrg}
        org={panelData}
        loading={panelLoading}
        error={panelError}
        onRetry={() =>
          setSearchParams({ org: selectedOrgId! }, { replace: true })
        }
        onManageSubscription={
          panelData?.subscription ? handleManageSubscription : undefined
        }
        onCancelSubscription={
          panelData?.subscription ? handleCancelSubscription : undefined
        }
      />

      {createOpen && role === 'partner' && (
        <CreateOrgModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            loadOrgs();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline modal for partner "Create organisation"
// Replaces the inline form that used to live on PartnerDashboard.
// ---------------------------------------------------------------------------

interface CreateOrgModalProps {
  onClose: () => void;
  onCreated: () => void;
}

function CreateOrgModal({ onClose, onCreated }: CreateOrgModalProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [planId, setPlanId] = useState('basic');
  const [billingMethod, setBillingMethod] = useState<'automated' | 'invoice'>(
    'automated',
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await partnerApiService.createOrganisation({
        name: name.trim(),
        billingEmail: email.trim(),
        planId,
        billingMethod,
      });
      onCreated();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to create organisation',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-40 cursor-default bg-base-content/30"
        onClick={onClose}
        aria-label="Close dialog"
        tabIndex={-1}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-org-title"
        className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-box border border-base-300 bg-base-100 p-6 shadow-xl"
      >
        <div className="mb-4 flex items-start justify-between">
          <h2
            id="create-org-title"
            className="text-lg font-bold text-base-content"
          >
            Create organisation
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-lg text-base-content/70 hover:text-base-content"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {error && (
          <div className="alert alert-error mb-4 text-sm" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label htmlFor="org-name" className="block pb-1 pl-1 text-sm">
              Organisation name
            </label>
            <input
              id="org-name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input input-bordered w-full"
              placeholder="Acme Inc"
              disabled={submitting}
            />
          </div>
          <div>
            <label htmlFor="org-email" className="block pb-1 pl-1 text-sm">
              Billing email
            </label>
            <input
              id="org-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input input-bordered w-full"
              placeholder="billing@acme.com"
              disabled={submitting}
            />
          </div>
          <div>
            <label htmlFor="org-plan" className="block pb-1 pl-1 text-sm">
              Plan
            </label>
            <select
              id="org-plan"
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              className="select select-bordered w-full"
              disabled={submitting}
            >
              <option value="basic">Basic</option>
              <option value="standard">Standard</option>
              <option value="premium">Premium</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
          <div>
            <label htmlFor="org-billing" className="block pb-1 pl-1 text-sm">
              Billing method
            </label>
            <select
              id="org-billing"
              value={billingMethod}
              onChange={(e) =>
                setBillingMethod(e.target.value as 'automated' | 'invoice')
              }
              className="select select-bordered w-full"
              disabled={submitting}
            >
              <option value="automated">Automated (Stripe)</option>
              <option value="invoice">Invoice</option>
            </select>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-ghost"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting || !name.trim() || !email.trim()}
            >
              {submitting ? (
                <span className="loading loading-spinner loading-sm" />
              ) : (
                'Create'
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
